import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { IonTextarea } from '@ionic/angular/standalone';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'crowdctrl-textarea',
  standalone: true,
  imports: [IonTextarea],
  templateUrl: './textarea.component.html',
  styleUrls: ['../form-outline-field-tokens.css', './textarea.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextareaComponent),
      multi: true,
    },
  ],
})
export class TextareaComponent implements ControlValueAccessor {
  @Input() placeholder?: string;
  @Input() label?: string;
  @Input() labelPlacement: string = 'stacked';
  @Input() helperText?: string;
  @Input() required = false;
  @Input() inputmode?: string;
  @Input() minlength?: number;
  @Input() maxlength?: number;
  @Input() name?: string;
  @Input() debounce?: number;
  @Input() rows?: number;
  @Input() autocapitalize: string = 'sentences';
  @Input() autocorrect?: 'on' | 'off' | string;
  @Input() autocomplete?: string;
  @Input() enterkeyhint?: string;
  @Input() spellcheck?: boolean;
  private _readonly = false;
  private _autoGrow = false;

  @Input()
  set readonly(value: boolean | string | null | undefined) {
    this._readonly = value === '' || value === true || value === 'true';
  }
  get readonly(): boolean {
    return this._readonly;
  }

  @Input('readOnly')
  set readOnly(value: boolean | string | null | undefined) {
    this.readonly = value;
  }

  @Input()
  set autoGrow(value: boolean | string | null | undefined) {
    this._autoGrow = value === '' || value === true || value === 'true';
  }
  get autoGrow(): boolean {
    return this._autoGrow;
  }

  @Input() disabled = false;

  @Output() ionInput = new EventEmitter<Event>();
  @Output() ionChange = new EventEmitter<Event>();
  @Output() ionBlur = new EventEmitter<Event>();
  @Output() ionFocus = new EventEmitter<Event>();

  @Input()
  set value(value: string | null) {
    this.writeValue(value);
  }

  currentValue: string | null = '';

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: string | null): void {
    this.currentValue = value ?? '';
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  handleInput(event: Event): void {
    const detailValue = (event as CustomEvent).detail as { value?: string } | undefined;
    const value = detailValue?.value ?? '';
    this.currentValue = value;
    this.onChange(value);
    this.ionInput.emit(event);
  }

  handleBlur(event: Event): void {
    this.onTouched();
    this.ionBlur.emit(event);
  }
}
